import React, { forwardRef, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import connectWhite from '@/assets/images/xero/connect-white.svg'
import disconnectWhite from '@/assets/images/xero/disconnect-white.svg'
import xeroWordmark from '@/assets/images/xero/xero-wordmark-blue.svg'

/*
 * Official Xero branding only (certification checkpoint 2): Xero's own
 * Connect/Disconnect button artwork and logo files, unmodified, and Xero's
 * Sign In widget. Never draw the Xero logo into a custom button.
 *   Buttons: https://developer.xero.com/static/otherfiles/Xero-Connect-Buttons.zip
 *   Logo:    https://www.xero.com/media/downloads/ (Xero wordmark)
 *   Widget:  https://developer.xero.com/documentation/xero-app-store/app-partner-guides/sign-in
 */

type ImageButtonProps = {
  onClick?: () => void
  loading?: boolean
  disabled?: boolean
  className?: string
}

const imageButtonClass =
  'inline-flex rounded-[3px] transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60'

export const XeroConnectButton = forwardRef<
  HTMLButtonElement,
  ImageButtonProps
>(({ onClick, loading = false, disabled = false, className, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    aria-busy={loading}
    className={cn(imageButtonClass, loading && 'animate-pulse', className)}
    {...rest}
  >
    <img src={connectWhite} alt="Connect to Xero" width={190} height={43} />
  </button>
))
XeroConnectButton.displayName = 'XeroConnectButton'

export const XeroDisconnectButton = forwardRef<
  HTMLButtonElement,
  ImageButtonProps
>(({ onClick, loading = false, disabled = false, className, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    aria-busy={loading}
    className={cn(imageButtonClass, loading && 'animate-pulse', className)}
    {...rest}
  >
    <img
      src={disconnectWhite}
      alt="Disconnect from Xero"
      width={222}
      height={43}
    />
  </button>
))
XeroDisconnectButton.displayName = 'XeroDisconnectButton'

export function XeroLogo({ className }: { className?: string }) {
  return (
    <img
      src={xeroWordmark}
      alt="Xero"
      className={cn('h-6 w-auto', className)}
    />
  )
}

const SSO_SCRIPT_SRC = 'https://edge.xero.com/platform/sso/xero-sso.js'

function loadXeroSsoScript() {
  if (document.querySelector(`script[src="${SSO_SCRIPT_SRC}"]`)) return
  const script = document.createElement('script')
  script.src = SSO_SCRIPT_SRC
  script.async = true
  script.defer = true
  document.head.appendChild(script)
}

/**
 * Xero's official "Sign in with Xero" widget. It is a <xero-sso> custom element
 * that reads its attributes once, when created — so it is inserted as HTML with
 * the attributes already set. The widget renders a plain link to `href`; the
 * click is intercepted to run our server-side PKCE start flow instead (the
 * `/auth/xero/start` route is the no-JS fallback).
 */
export function XeroSignInWidget({
  label,
  onStart,
  className,
}: {
  label: string
  onStart: () => void
  className?: string
}) {
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!container.current) return
    const el = document.createElement('xero-sso')
    Object.entries({
      href: '/auth/xero/start',
      label,
      theme: 'light',
      // 'large' is the only size that takes a height; 44px matches the other buttons.
      size: 'large',
      height: '44',
      layout: 'block',
      // Xero's own options, set to match the other sign-in buttons (equal prominence).
      'border-radius': '4',
      'border-color': '#747775',
      font: 'inherit',
      'no-track': 'true',
    }).forEach(([key, value]) => el.setAttribute(key, value))
    // Parse as HTML so the element upgrades with its attributes present.
    container.current.innerHTML = el.outerHTML
    loadXeroSsoScript()
  }, [label])

  return (
    <div
      ref={container}
      className={cn("w-full font-['Google_Sans',Inter,sans-serif]", className)}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('a')) {
          event.preventDefault()
          onStart()
        }
      }}
    />
  )
}
